package com.evoapps.evoshift.domain.usecase.owner

import com.evoapps.evoshift.domain.model.InvalidSharedCostException
import com.evoapps.evoshift.domain.model.MonthlyResult
import com.evoapps.evoshift.domain.model.MonthlyResultSide
import com.evoapps.evoshift.domain.model.RosterFinancials
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.SharedCost
import com.evoapps.evoshift.domain.model.SharedCostAllocation
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.SharedCostRepository
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

/** SH-131: the owner's shared costs of the business. */
class GetSharedCostsUseCase(private val repository: SharedCostRepository) {
    suspend operator fun invoke(ownerId: String): Result<List<SharedCost>> = repository.getSharedCosts(ownerId)
}

/**
 * SH-136: says that the owner's shared costs changed (saved, converted or deleted), so screens
 * still open behind the shared costs screen (the profile's count, the Money screen's month, whose
 * ledger holds the workplace's part) read them again when the owner comes back. One per app.
 */
class SharedCostChanges {
    private val _version = MutableStateFlow(0)

    /** Goes up by one on every change; screens react to the values after the one they started with. */
    val version: StateFlow<Int> = _version.asStateFlow()

    fun changed() = _version.update { it + 1 }
}

/**
 * SH-131: saves a shared cost after the same checks the server makes (the split adds up, a date
 * or a starting month, a basis), so the editor can say what is wrong before a round trip.
 */
class SaveSharedCostUseCase(
    private val repository: SharedCostRepository,
    /** SH-136: told after a save, so other open screens refresh. */
    private val changes: SharedCostChanges? = null
) {
    suspend operator fun invoke(cost: SharedCost, replacesRuleId: String? = null): Result<String> = runCatching {
        val trimmed = cost.copy(name = cost.name.trim())
        val problems = SharedCostAllocation.problems(trimmed)
        if (problems.isNotEmpty()) throw InvalidSharedCostException(problems)
        repository.saveSharedCost(trimmed, replacesRuleId).getOrThrow()
    }.onSuccess { changes?.changed() }
}

class DeleteSharedCostUseCase(
    private val repository: SharedCostRepository,
    /** SH-136: told after a delete, so other open screens refresh. */
    private val changes: SharedCostChanges? = null
) {
    suspend operator fun invoke(costId: String): Result<Unit> = repository.deleteSharedCost(costId).onSuccess { changes?.changed() }
}

/** One workplace's month in the business totals; [financials] null when it has no totals yet. */
data class WorkplaceMonth(
    val workplaceId: String,
    val financials: RosterFinancials?,
    /** The forecast side of its result (actual where the takings are in, plan for the rest). */
    val result: MonthlyResultSide?
)

/**
 * SH-131: a month across the owner's workplaces. Every amount is the sum of the workplaces'
 * results, and the shared costs are inside them (each workplace's part is a cost of its month),
 * so [sharedCosts] is "of which", never added on top: nothing is counted twice.
 */
data class BusinessMonth(val year: Int, val month: Int, val workplaces: List<WorkplaceMonth>) {
    private val sides get() = workplaces.mapNotNull { it.result }
    val revenue: Double get() = sides.sumOf { it.revenue }
    val labourCost: Double get() = sides.sumOf { it.labourCost }
    val costs: Double get() = sides.sumOf { it.costs }
    val sharedCosts: Double get() = sides.sumOf { it.sharedCosts }
    val result: Double get() = revenue - costs
    val hasFigures: Boolean get() = sides.isNotEmpty()
}

/**
 * SH-131: the owner's month across [workplaceIds]: each one's totals (roster_financials) and ledger
 * (for the shared parts), read at the same time. A workplace without that month counts nothing.
 */
class GetBusinessMonthUseCase(private val financialRepository: FinancialRepository) {
    suspend operator fun invoke(workplaceIds: List<String>, year: Int, month: Int): Result<BusinessMonth> = runCatching {
        val months = coroutineScope {
            workplaceIds.map { workplaceId ->
                async {
                    val rosterId = RosterIds.of(workplaceId, year, month)
                    val financials = financialRepository.getRosterFinancials(rosterId).getOrNull()
                    val costs = if (financials == null) emptyList() else financialRepository.getFixedCosts(rosterId).getOrDefault(emptyList())
                    WorkplaceMonth(workplaceId, financials, financials?.let { MonthlyResult.of(it, costs).forecast })
                }
            }.awaitAll()
        }
        BusinessMonth(year, month, months)
    }
}
